
// ./components/Chat.jsx
import React, { useState, useEffect, useRef, memo } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, User, Send, MessageCircleQuestionIcon, HandCoins, X, Key } from "lucide-react"; // Import des icônes
import axiosInstance from "./AxiosInstance"; // Importer l'instance d'axios
import useMediaQuery from "../hooks/useMediaQuery";

const Chat = memo(({ onChat, loading, setRetour, backia, gameState, inputMessage, setInputMessage, chatStatus, pendingInvestments, mapActions, canValidate, actionMessage, setActionMessage, redirect, turnSubmitted = false }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const textareaRef = useRef(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const fetchExplanation = async () => {
    try {
      const response = await axiosInstance.get('/chat/explanation');
      setExplanation(response.data);
    } catch (error) {
      console.error("Erreur lors de la récupération de l'explication du chat:", error);
    }
  };

  const toggleExplanation = () => {
    if (!explanation) {
      fetchExplanation();
    }
    setShowExplanation(!showExplanation);
  };

  const login = () => {
    navigate("/login");
  };

  useEffect(() => {
    const initialMessage = {
      type: "assistant",
      content: "Bonjour, je suis votre IA stratégique. Vous pouvez effectuer jusqu'à 3 actions par tour. Vous pouvez investir dans les paramètres, attaquer un pays ou vous défendre contre une attaque.",
    };
    if (Array.isArray(gameState?.chat) && gameState.chat.length > 0) {
      setMessages(gameState.chat.map(msg => ({ type: msg.type, content: msg.message })));
    } else {
      setMessages([initialMessage]);
    }
  }, [gameState?.chat]);

  useEffect(() => {
    const totalActions = pendingInvestments.length + mapActions.length;

    const investmentMessage = pendingInvestments.length > 0
      ? `💰 Investissements : ${pendingInvestments.join(', ')}`
      : '';

    const mapMessage = mapActions.length > 0
      ? `⚔️🛡️ Actions : ${mapActions.map(a => `${a.action} ${a.country}`).join(', ')}`
      : '';

    let messageContent = [investmentMessage, mapMessage].filter(Boolean).join('. ');

    if (totalActions >= 3) {
      messageContent += (messageContent ? '. ' : '') + "⚠️ Limite de 3 actions totales atteinte.";
    }

    setMessages(prevMessages => {
      const otherMessages = prevMessages.filter(msg => msg.id !== 'action-summary');
      if (messageContent) {
        return [
          ...otherMessages,
          { id: 'action-summary', type: 'user', content: messageContent }
        ];
      } else {
        return otherMessages;
      }
    });
  }, [pendingInvestments, mapActions]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const minHeight = 48;
      const maxHeight = 120;
      textareaRef.current.style.height = `${Math.min(Math.max(scrollHeight, minHeight), maxHeight)}px`;
    }
  }, [inputMessage]);

  const handleSend = () => {
    if (inputMessage.trim() !== "") {
      onChat(inputMessage);
      setMessages(prev => [...prev, { type: "user", content: inputMessage }]);
      setInputMessage("");
    } else if (canValidate) {
      const investmentMessage = pendingInvestments.length > 0
        ? `💰 Investissements : ${pendingInvestments.join(', ')}`
        : '';

      const mapMessage = mapActions.length > 0
        ? `⚔️🛡️ Actions : ${mapActions.map(a => `${a.action} ${a.country}`).join(', ')}`
        : '';

      let messageContent = [investmentMessage, mapMessage].filter(Boolean).join('. ');
      messageContent += (messageContent ? '. ' : '') + "Limite de 3 actions.";
      onChat(messageContent);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const [isNight, setIsNight] = useState(false);

  useEffect(() => {
    if (backia !== "") {
      setMessages(prev => {
        // Check if the last message is the same as backia to prevent duplicates
        if (prev.length > 0 && prev[prev.length - 1].content === backia) {
          return prev;
        }
        return [...prev, { type: "assistant", content: backia }];
      });
      setRetour(""); // Clear backia after processing
    }
  }, [backia, setRetour]);

  useEffect(() => {
    const checkTime = () => {
      const hour = new Date().getHours();
      setIsNight(hour >= 23 || hour < 8);
    };
    checkTime();
    const interval = setInterval(checkTime, 60000); // vérifie toutes les minutes
    return () => clearInterval(interval);
  }, []);

  if (loading && messages.length === 0) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-slate-300 text-lg">Chargement de l'interface de chat...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${isMobile ? 'h-full' : 'h-80'} bg-gradient-to-br from-slate-900/95 to-slate-800/95 backdrop-blur-sm border-t border-slate-700/50`}>
      {showExplanation && explanation && (
        <div className="fixed transform -translate-y-80 inset-0 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg p-6 max-w-4xl w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-white">{explanation.title}</h2>
              <button onClick={toggleExplanation} className="text-slate-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <p className="text-slate-300 mb-4">{explanation.introduction}</p>
            <div className="space-y-4">
              {explanation.sections.map((section, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold text-blue-400 mb-1">{section.title}</h3>
                  <p className="text-slate-300">{section.content}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="h-full flex flex-col">
        {/* Chat Header */}
        <div className="flex items-center gap-3 p-4 border-b border-slate-700/50">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <Bot size={20} className="text-white" />
            </div>
            <div>
              <div className="font-semibold text-white">Votre 1er IA Ministre </div>
              <div className="text-xs text-green-400 flex items-center gap-1">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                En ligne{isNight ? ", je ne dors jamais par contre vous allez dormir !" : " Mode jour actif"}
              </div>
            </div>
          </div>
          <div className="ml-auto flex gap-2">
            <button onClick={toggleExplanation} className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors">
              <MessageCircleQuestionIcon size={25} className="text-blue-400" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div
          className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar"
          ref={el => {
            if (el) {
              el.scrollTop = el.scrollHeight;
            }
          }}
        >
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex gap-3 ${msg.type === 'user' ? 'justify-end' : ''}`}>
              {msg.type === 'assistant' && (
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                  {typeof msg.content === 'string' && msg.content.startsWith("Investissements en cours") ? (
                    <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-black rounded-full flex items-center justify-center flex-shrink-0">
                      <HandCoins size={16} className="text-yellow-400" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Bot size={16} className="text-white" />
                    </div>
                  )}
                </div>
              )}
              <div className={`max-w-7xl p-3 rounded-2xl text-base/5 whitespace-pre-wrap ${msg.type === 'user'
                ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white'
                : 'bg-gradient-to-br from-slate-700 to-slate-800 text-slate-100'
                }`}>
                {msg.content}
              </div>
              {msg.type === 'user' && (
                <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <User size={16} className="text-white" />
                </div>
              )}
            </div>
          ))}
          {chatStatus && (
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <Bot size={16} className="text-white" />
              </div>
              <div className="bg-gradient-to-br from-slate-700 to-slate-800 p-3 rounded-2xl">
                <div className="flex items-center gap-2 text-slate-300">
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                  {chatStatus !== "" && (
                    <span>{chatStatus}</span>
                  )}
                  {redirect && (
                    <a onClick={login} className="inline-flex items-center cursor-pointer gap-2 rounded-xl bg-gradient-to-r from-rose-700 via-blue-500 to-violet-500 px-4 py-2 text-sm font-semibold shadow-lg hover:opacity-95 active:scale-[.98]">
                      <Key size={14} />
                      Configurer votre clé API
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="p-4 border-t border-slate-700/50">

          {actionMessage && (
            <div className="flex items-center justify-center p-2 bg-blue-900/50 rounded-lg mb-2">
              <p className="text-white text-sm">{actionMessage.text}</p>
              {actionMessage.validate ? (
                <button
                  onClick={handleSend}
                  className="ml-4 px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  Valider
                </button>
              ) : (
                <button
                  onClick={() => setActionMessage(null)}
                  className="ml-2 text-slate-400 hover:text-white"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          )}

          <div className="flex gap-2 items-end">
            <div className="flex-1 relative">
              <textarea
                ref={textareaRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={turnSubmitted ? "Actions soumises - en attente des autres joueurs..." : "Demandez conseil à votre IA stratégique..."}
                disabled={turnSubmitted}
                className={`w-full bg-slate-800/50 border border-slate-600 rounded-xl px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none overflow-hidden min-h-[48px] max-h-[120px] leading-5 ${turnSubmitted ? 'opacity-50 cursor-not-allowed' : ''}`}
                rows={1}
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#475569 transparent'
                }}
              />
            </div>
            <button
              onClick={handleSend}
              disabled={turnSubmitted || loading || (!inputMessage.trim() && !canValidate)}
              className={`p-3 rounded-xl transition-all duration-300 flex-shrink-0 ${turnSubmitted || loading || (!inputMessage.trim() && !canValidate)
                ? 'bg-slate-600 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 hover:shadow-lg hover:shadow-blue-500/25'
                }`}
            >
              <Send size={20} className={`text-white ${!loading && (!inputMessage.trim() || canValidate) ? 'hover:animate-pulse' : ''}`} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default Chat;
